import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { signIn } from '@/lib/auth';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    const res = await signIn.email({ email, password });
    setPending(false);
    if (res.error) {
      const message = res.error.message ?? 'Sign-in failed';
      setError(message);
      toast.error(message);
      return;
    }
    toast.success('Signed in');
    navigate('/dashboard');
  }

  async function social(provider: 'google' | 'github') {
    const res = await signIn.social({ provider, callbackURL: '/dashboard' });
    if (res?.error) {
      toast.error(res.error.message ?? `${provider} sign-in failed`);
    }
  }

  return (
    <div className="grid h-full place-items-center bg-muted/40">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1">
          <CardTitle>Sign in to Storeflare</CardTitle>
          <CardDescription>Welcome back.</CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </CardContent>
          <CardFooter className="flex-col gap-3 pt-0">
            <Button type="submit" className="w-full" disabled={pending}>
              {pending ? 'Signing in…' : 'Sign in'}
            </Button>
            <div className="flex w-full items-center gap-2">
              <Separator className="shrink" />
              <span className="text-xs text-muted-foreground">or</span>
              <Separator className="shrink" />
            </div>
            <div className="grid w-full grid-cols-2 gap-2">
              <Button variant="outline" type="button" onClick={() => social('google')}>
                Google
              </Button>
              <Button variant="outline" type="button" onClick={() => social('github')}>
                GitHub
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              No account?{' '}
              <Link to="/signup" className="font-medium text-foreground underline-offset-4 hover:underline">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
